/**
 * Commander Design System - Typography
 *
 * Type stack (P6.5 — Calm Control Room): Geist (sans) + JetBrains Mono
 * (monospace), self-hosted via @fontsource-variable. Forwarded from
 * @commander/tokens via CSS custom properties so a single token swap
 * propagates everywhere.
 *
 * `display` no longer points at a condensed face — Geist itself reads tightly
 * enough at heading sizes that a separate display family is unnecessary.
 * Kept as an alias for the sans stack so existing callers still compile.
 */

export const fontFamily = {
  /** Primary UI font — Geist via @commander/tokens */
  sans: 'var(--font-sans)',
  /** Monospace for IDs, DMR codes, technical values — JetBrains Mono */
  mono: 'var(--font-mono)',
  /** Display alias — same as sans; legacy callers may still reference it */
  display: 'var(--font-sans)',
} as const;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const fontSize = {
  /** 11px - Tiny labels, node counts */
  xs: '0.6875rem',
  /** 13px - Small captions, secondary text */
  sm: '0.8125rem',
  /** 15px - Body text */
  md: '0.9375rem',
  /** 17px - Large body, emphasis */
  lg: '1.0625rem',
  /** 20px - Small headings */
  xl: '1.25rem',
  /** 24px - Section headings */
  '2xl': '1.5rem',
  /** 30px - Page titles */
  '3xl': '1.875rem',
  /** 36px - Hero text */
  '4xl': '2.25rem',
  /** 48px - Display */
  '5xl': '3rem',
} as const;

export const lineHeight = {
  /** Tight - headings, condensed text */
  tight: 1.2,
  /** Snug - subheadings */
  snug: 1.35,
  /** Normal - body text */
  normal: 1.5,
  /** Relaxed - large text blocks */
  relaxed: 1.65,
} as const;

export const letterSpacing = {
  /** Tight - large headings */
  tight: '-0.02em',
  /** Normal - body text */
  normal: '0',
  /** Wide - small caps, labels */
  wide: '0.02em',
  /** Wider - uppercase labels */
  wider: '0.05em',
  /** Widest - status indicators */
  widest: '0.1em',
} as const;

/** Kiosk-specific typography overrides for readability */
export const kioskTypography = {
  /** Base font size for kiosk UI (20px) */
  baseFontSize: '1.25rem',
  /** Minimum readable size (16px) */
  minFontSize: '1rem',
  /** Large button text (24px) */
  buttonFontSize: '1.5rem',
  /** Feedback message text (32px) */
  feedbackFontSize: '2rem',
} as const;

export const typography = {
  fontFamily,
  fontWeight,
  fontSize,
  lineHeight,
  letterSpacing,
  kiosk: kioskTypography,
} as const;
